import type {
  VolumeTrend,
  ChangeLogEntry,
  Partner,
  PartnerActivityEntry,
  PartnerUsage,
  Tier,
} from "./model";
import type { MetricsPortfolio } from "./metrics.types";

/** Partner overrides where `usage` may itself be partial (merged over the default usage). */
export type PartnerOverrides = Partial<Omit<Partner, "usage">> & {
  usage?: Partial<PartnerUsage>;
};

/** Pairs two equal-length daily series into day rows; unequal fixtures fail loudly. */
const makeVolumeTrend = ({ saved, adhoc }: { saved: number[]; adhoc: number[] }): VolumeTrend => {
  if (saved.length !== adhoc.length) throw new Error("Fixture trend series differ in length");
  return saved.map((value, index) => ({ day: index + 1, saved: value, adhoc: adhoc[index] }));
};

/**
 * Stub portfolio for the Metrics Dashboard, ported from the POC's MOCK DATA
 * block (the four partners: Ad Impact, Zappi, Agility PR, Orllo). This is the
 * *only* place fake partner data lives — `metrics.api.ts` serves it today and
 * will stop importing this file once `customer-portal-services` is wired.
 *
 * `make<Type>` builders follow insights-home's fixtures convention: a valid
 * default, spread `overrides` last, so a test states only the field it cares
 * about.
 *
 * `callVolumeSeries` carries 28 points (two 14-day windows) so a 7- or 14-day
 * growth window can be computed off the stub; a 30-day window would need the
 * 60-point series the backend is expected to return.
 *
 * `volumeTrend` carries the full 30 days it's named for (a separate series
 * from `callVolumeSeries`, split Saved vs Ad Hoc) — it feeds the deep dive's
 * trend chart, not `computeGrowth`.
 */

/** A default, valid activity row; every fixture below (and any test) overrides the fields it cares about. */
export const makeActivityEntry = (
  overrides: Partial<PartnerActivityEntry> = {}
): PartnerActivityEntry => ({
  time: "08/12 07:00",
  type: "saved",
  query: "—",
  region: "US",
  resultCount: 10,
  status: "Hit",
  ...overrides,
});

export const makeTier = (overrides: Partial<Tier> = {}): Tier => ({
  id: "starter",
  name: "Starter",
  savedCap: 10,
  adhocCap: 250,
  queryLen: 2000,
  regions: { US: true, UK: false, Canada: false, Netherlands: false, France: false, Spain: false },
  ...overrides,
});

const DEFAULT_USAGE: PartnerUsage = {
  saved: { used: 3, cap: 10 },
  adhoc: { used: 40, cap: 250 },
  firehoseEnabled: false,
  mediaPlayerViews30d: 100,
  callVolumeSeries: [
    10, 9, 11, 10, 12, 9, 10, 11, 10, 9, 11, 10, 10, 9, 10, 9, 11, 10, 12, 9, 10, 11, 10, 9, 11, 10,
    10, 9,
  ],
  volumeTrend: makeVolumeTrend({
    saved: [
      7, 8, 8, 8, 8, 8, 7, 7, 6, 6, 6, 6, 7, 7, 8, 8, 8, 8, 8, 7, 6, 6, 6, 6, 6, 7, 8, 8, 8, 8,
    ],
    adhoc: [
      40, 40, 38, 35, 32, 29, 27, 26, 26, 28, 30, 34, 37, 39, 40, 40, 39, 36, 33, 30, 28, 26, 26,
      27, 29, 32, 35, 38, 40, 40,
    ],
  }),
  recentActivity: [
    makeActivityEntry(),
    makeActivityEntry(),
    makeActivityEntry(),
    makeActivityEntry(),
  ],
};

export const makePartner = (overrides: PartnerOverrides = {}): Partner => ({
  id: "example",
  name: "Example Partner",
  initials: "EX",
  partnerId: "PTN-000000",
  tierId: "starter",
  accountManager: "Jane Doe",
  acv: 100_000,
  since: "2024",
  renewalDate: "2027-01-01",
  flag: { label: "Healthy", type: "success" },
  ...overrides,
  usage: { ...DEFAULT_USAGE, ...overrides.usage },
});

export const STUB_TIERS: Tier[] = [
  makeTier({ id: "starter", name: "Starter", savedCap: 10, adhocCap: 250, queryLen: 2000 }),
  makeTier({
    id: "growth",
    name: "Growth",
    savedCap: 25,
    adhocCap: 750,
    queryLen: 8000,
    regions: { US: false, UK: true, Canada: true, Netherlands: false, France: false, Spain: true },
  }),
  makeTier({
    id: "enterprise",
    name: "Enterprise",
    savedCap: 100,
    adhocCap: 5000,
    queryLen: 50000,
    regions: { US: true, UK: true, Canada: true, Netherlands: true, France: true, Spain: true },
  }),
];

export const STUB_PARTNERS: Partner[] = [
  makePartner({
    id: "adimpact",
    name: "Ad Impact",
    initials: "AI",
    partnerId: "PTN-100245",
    tierId: "enterprise",
    accountManager: "Jane Doe",
    acv: 1_500_000,
    since: "2019",
    renewalDate: "2027-01-15",
    flag: { label: "Approaching Limit — Upsell", type: "warning" },
    usage: {
      saved: { used: 9, cap: 10 },
      adhoc: { used: 420, cap: 500 },
      firehoseEnabled: true,
      mediaPlayerViews30d: 4820,
      callVolumeSeries: [
        78, 82, 80, 85, 83, 88, 86, 90, 88, 93, 91, 89, 94, 90, 92, 98, 101, 96, 104, 99, 108, 110,
        115, 112, 119, 121, 118, 126,
      ],
      volumeTrend: makeVolumeTrend({
        saved: [
          55, 60, 63, 65, 64, 61, 56, 52, 48, 45, 46, 48, 52, 57, 62, 64, 65, 63, 59, 54, 50, 46,
          45, 46, 50, 54, 59, 63, 65, 64,
        ],
        adhoc: [
          415, 408, 388, 360, 327, 298, 276, 266, 269, 286, 313, 345, 376, 400, 413, 413, 398, 373,
          342, 310, 284, 268, 266, 277, 300, 331, 363, 391, 409, 415,
        ],
      }),
      // Ported from the POC's PARTNERS.adimpact.activity, newest first. Two
      // PageFeed rows are remapped to "adhoc" — PageFeed isn't a modeled
      // ApiKind here — preserving the original 8 Hit / 3 Error (27%) split.
      recentActivity: [
        makeActivityEntry({
          time: "08/12 07:29",
          type: "firehose",
          query: "GET /firehose/stream?token=…",
          resultCount: null,
        }),
        makeActivityEntry({
          time: "08/12 07:14",
          type: "saved",
          query: '"Ad Impact" OR "AdImpact Media"',
          resultCount: 24,
        }),
        makeActivityEntry({
          time: "08/12 06:58",
          type: "adhoc",
          query: "station_type=2 AND market=NY",
          resultCount: 86,
        }),
        makeActivityEntry({
          time: "08/12 06:41",
          type: "saved",
          query: '"client renewal" AND "Q3"',
          resultCount: 15,
        }),
        makeActivityEntry({
          time: "08/12 05:20",
          type: "adhoc",
          query: "GET /feed/page?token=…",
          status: "Error",
          resultCount: 0,
        }),
        makeActivityEntry({
          time: "08/12 04:47",
          type: "adhoc",
          query: 'station_market:NY AND "Ad Impact"',
          resultCount: 102,
        }),
        makeActivityEntry({
          time: "08/12 03:12",
          type: "adhoc",
          query: "GET /feed/page?token=…",
          status: "Error",
          resultCount: 0,
        }),
        makeActivityEntry({
          time: "08/12 01:35",
          type: "saved",
          query: '"J. Lindqvist" OR "CEO Ad Impact"',
          resultCount: 6,
        }),
        makeActivityEntry({
          time: "08/11 22:03",
          type: "saved",
          query: '"Ad Impact" AND spend',
          resultCount: 19,
        }),
        makeActivityEntry({
          time: "08/11 20:14",
          type: "adhoc",
          query: 'source_type:podcast AND "Ad Impact"',
          status: "Error",
          resultCount: 0,
        }),
        makeActivityEntry({
          time: "08/11 18:02",
          type: "saved",
          query: '"MediaMetrics" OR "BrandWatch"',
          resultCount: 33,
        }),
      ],
    },
  }),
  makePartner({
    id: "zappi",
    name: "Zappi",
    initials: "ZP",
    partnerId: "PTN-100612",
    tierId: "growth",
    accountManager: "Jane Doe",
    acv: 410_000,
    since: "2023",
    renewalDate: "2026-09-30",
    flag: { label: "Contract Breach — Ad Hoc Cap Exceeded", type: "error" },
    usage: {
      saved: { used: 6, cap: 10 },
      adhoc: { used: 285, cap: 250 },
      firehoseEnabled: true,
      mediaPlayerViews30d: 1970,
      callVolumeSeries: [
        48, 50, 49, 52, 51, 54, 53, 55, 54, 57, 56, 58, 57, 59, 60, 58, 62, 65, 61, 64, 63, 66, 68,
        67, 70, 69, 72, 71,
      ],
      volumeTrend: makeVolumeTrend({
        saved: [
          35, 38, 40, 41, 41, 39, 36, 33, 30, 29, 29, 31, 33, 36, 39, 41, 41, 40, 38, 35, 32, 29,
          29, 29, 32, 35, 38, 40, 41, 41,
        ],
        adhoc: [
          281, 276, 263, 243, 222, 201, 186, 180, 182, 194, 212, 234, 254, 271, 280, 279, 270, 253,
          231, 210, 192, 182, 180, 188, 203, 224, 245, 264, 277, 281,
        ],
      }),
      // Ported from the POC's PARTNERS.zappi.activity, newest first. The one
      // PageFeed row is remapped to "adhoc" (see the Ad Impact note above),
      // preserving the original 4 Hit / 1 Error (20%) split.
      recentActivity: [
        makeActivityEntry({
          time: "08/12 04:02",
          type: "firehose",
          region: "UK",
          query: "GET /firehose/stream?token=…",
          resultCount: null,
        }),
        makeActivityEntry({
          time: "08/12 03:10",
          type: "saved",
          region: "UK",
          query: '"Zappi" broadcast ad detection',
          resultCount: 18,
        }),
        makeActivityEntry({
          time: "08/12 02:44",
          type: "adhoc",
          region: "Canada",
          query: "country=CA market=Toronto",
          resultCount: 64,
        }),
        makeActivityEntry({
          time: "08/11 19:12",
          type: "saved",
          region: "Spain",
          query: '"Zappi España"',
          resultCount: 11,
        }),
        makeActivityEntry({
          time: "08/11 18:02",
          type: "adhoc",
          region: "UK",
          query: "GET /feed/page?token=…",
          status: "Error",
          resultCount: 0,
        }),
      ],
    },
  }),
  makePartner({
    id: "agilitypr",
    name: "Agility PR",
    initials: "AG",
    partnerId: "PTN-100078",
    tierId: "starter",
    accountManager: "Chris Thomas",
    acv: 85_000,
    since: "2021",
    renewalDate: "2027-03-01",
    flag: { label: "Healthy", type: "success" },
    usage: {
      saved: { used: 3, cap: 10 },
      adhoc: { used: 64, cap: 250 },
      firehoseEnabled: false,
      mediaPlayerViews30d: 410,
      callVolumeSeries: [
        10, 9, 11, 10, 12, 9, 10, 11, 10, 9, 11, 10, 10, 9, 10, 9, 11, 10, 12, 9, 10, 11, 10, 9, 11,
        10, 10, 9,
      ],
      volumeTrend: makeVolumeTrend({
        saved: [
          7, 8, 8, 8, 8, 8, 7, 7, 6, 6, 6, 6, 7, 7, 8, 8, 8, 8, 8, 7, 6, 6, 6, 6, 6, 7, 8, 8, 8, 8,
        ],
        adhoc: [
          67, 66, 63, 58, 53, 48, 45, 43, 44, 46, 51, 56, 61, 65, 67, 67, 64, 60, 55, 50, 46, 43,
          43, 45, 49, 53, 59, 63, 66, 67,
        ],
      }),
      // Ported from the POC's PARTNERS.agilitypr.activity, newest first.
      recentActivity: [
        makeActivityEntry({
          time: "08/12 01:02",
          type: "saved",
          query: '"Spotify" -> email digest',
          resultCount: 9,
        }),
        makeActivityEntry({
          time: "08/11 14:38",
          type: "adhoc",
          query: "station_media_type=2",
          resultCount: 47,
        }),
        makeActivityEntry({
          time: "08/11 09:07",
          type: "saved",
          query: '"Doug Whie" OR "Doug Valley"',
          resultCount: 14,
        }),
      ],
    },
  }),
  makePartner({
    id: "orllo",
    name: "Orllo",
    initials: "OR",
    partnerId: "PTN-100901",
    tierId: "growth",
    accountManager: "Jane Doe",
    acv: 120_000,
    since: "2026",
    renewalDate: "2027-02-01",
    flag: { label: "New Account — Log Data Under Review", type: "warning" },
    usage: {
      saved: { used: 2, cap: 25 },
      adhoc: { used: 60, cap: 750 },
      firehoseEnabled: true,
      mediaPlayerViews30d: 95,
      callVolumeSeries: [
        3, 4, 2, 5, 4, 6, 3, 5, 7, 4, 6, 8, 5, 7, 8, 6, 10, 7, 9, 12, 8, 14, 18, 16, 22, 19, 24, 21,
      ],
      volumeTrend: makeVolumeTrend({
        saved: [
          2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
        ],
        adhoc: [
          55, 54, 51, 48, 43, 39, 36, 35, 36, 38, 41, 46, 50, 53, 55, 55, 53, 49, 45, 41, 38, 36,
          35, 37, 40, 44, 48, 52, 54, 55,
        ],
      }),
      // Ported from the POC's PARTNERS.orllo.activity, newest first — a new
      // account with mostly malformed onboarding requests (empty query),
      // matching Orllo's "Log Data Under Review" flag.
      recentActivity: [
        makeActivityEntry({
          time: "08/12 08:10",
          type: "firehose",
          query: "GET /firehose/stream?token=…",
          resultCount: null,
        }),
        makeActivityEntry({
          time: "08/12 07:55",
          type: "saved",
          query: "—",
          status: "Error",
          resultCount: 0,
        }),
        makeActivityEntry({
          time: "08/12 07:55",
          type: "saved",
          query: "—",
          status: "Error",
          resultCount: 0,
        }),
        makeActivityEntry({
          time: "08/12 07:54",
          type: "saved",
          query: "—",
          status: "Error",
          resultCount: 0,
        }),
        makeActivityEntry({
          time: "08/12 07:53",
          type: "saved",
          query: "onboarding test query",
          resultCount: 3,
        }),
        makeActivityEntry({
          time: "08/12 07:50",
          type: "saved",
          query: "—",
          status: "Error",
          resultCount: 0,
        }),
      ],
    },
  }),
];

/** Illustrative history, matching the POC's seeded CHANGE_LOG. */
export const STUB_CHANGE_LOG: ChangeLogEntry[] = [
  {
    time: "08/10 14:32",
    partner: "Ad Impact",
    field: "Saved search cap",
    from: "8",
    to: "10",
    by: "Jane Doe",
  },
  {
    time: "08/09 09:15",
    partner: "Zappi",
    field: "Notify threshold (%) — Ad Hoc",
    from: "80",
    to: "85",
    by: "Jane Doe",
  },
  {
    time: "08/05 16:02",
    partner: "Growth (tier)",
    field: "Tier updated",
    from: "—",
    to: "Regions included: added Spain",
    by: "Chris Thomas",
  },
  {
    time: "08/01 11:40",
    partner: "Orllo",
    field: "Package tier",
    from: "Starter",
    to: "Growth",
    by: "Jane Doe",
  },
];

export const STUB_PORTFOLIO: MetricsPortfolio = {
  partners: STUB_PARTNERS,
  tiers: STUB_TIERS,
  changeLog: STUB_CHANGE_LOG,
};
